<template>
  <div
    class="knowledge-source-card"
    :class="{ 'knowledge-source-card--text-only': hideThumbnail }"
  >
    <div
      v-if="!hideThumbnail"
      class="knowledge-source-card__thumb"
      :class="{ 'knowledge-source-card__thumb--article': isArticle }"
    >
      <template v-if="isArticle">
        <div class="knowledge-source-card__article-placeholder" aria-hidden="true">
          <img :src="ARTICLE_ICON_SRC" alt="" class="knowledge-source-card__article-icon" />
        </div>
      </template>
      <template v-else>
        <v-img :src="thumbnailUrl" cover height="100%" class="knowledge-source-card__thumb-img" />
        <div class="knowledge-source-card__video-overlay" aria-hidden="true">
          <img :src="videoOverlaySrc" alt="" class="knowledge-source-card__overlay-graphic" />
        </div>
      </template>
    </div>
    <div class="knowledge-source-card__body">
      <p v-if="displayDescription" class="knowledge-source-card__description">{{ displayDescription }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

/** Design handoff: composite play + audio + TOC/step markers (see ER-8917). */
const OVERLAY_VIDEO_WITH_TOC = require("@components/assets/KnowledgeSourceCard-video-with-toc-overlay.svg");
const OVERLAY_VIDEO_STEP_BY_STEP = require("@components/assets/KnowledgeSourceCard-video-step-by-step-overlay.svg");
const ARTICLE_ICON_SRC = require("@components/assets/KnowledgeSourceCard-article-icon.svg");

const props = defineProps({
  /** Display label from product spec: `Video with TOC`, `Video Step by Step`, or `Article`. */
  type: {
    type: String,
    required: true,
    validator: (v) => ["Video with TOC", "Video Step by Step", "Article"].includes(v),
  },
  /**
   * Thumbnail URL (e.g. Cloudflare Stream `videodelivery.net` still image).
   * Ignored when `type` is `Article`.
   */
  thumbnailUrl: {
    type: String,
    default: "",
  },
  /** May contain HTML from the API; shown as plain text (tags like `<p></p>` are not rendered literally). */
  description: {
    type: String,
    default: "",
  },
  /** When true, no thumbnail / article / video column (copy-only row, e.g. MDP-452 NONE). */
  hideThumbnail: {
    type: Boolean,
    default: false,
  },
});

const isArticle = computed(() => props.type === "Article");

/** Strip HTML so `{{ }}` does not show raw tags; empty markup becomes blank. */
function htmlToPlainText(html) {
  if (!html || typeof html !== "string") return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const text = doc.body?.textContent ?? "";
    return text.replace(/\s+/g, " ").trim();
  } catch {
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
}

const displayDescription = computed(() => htmlToPlainText(props.description));

const videoOverlaySrc = computed(() => {
  if (props.type === "Video Step by Step") return OVERLAY_VIDEO_STEP_BY_STEP;
  if (props.type === "Video with TOC") return OVERLAY_VIDEO_WITH_TOC;
  return "";
});
</script>

<style scoped>
.knowledge-source-card {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.knowledge-source-card--text-only {
  gap: 0;
}

.knowledge-source-card--text-only .knowledge-source-card__body {
  padding-top: 0;
}

.knowledge-source-card__thumb {
  position: relative;
  flex: 0 0 auto;
  width: 153px;
  height: 89px;
  border-radius: 8px;
  overflow: hidden;
}

.knowledge-source-card__thumb--article {
  background: transparent;
  overflow: visible;
}

.knowledge-source-card__thumb-img {
  width: 100%;
  height: 100%;
}

.knowledge-source-card__thumb-img :deep(.v-image__image),
.knowledge-source-card__thumb-img :deep(img) {
  object-fit: cover;
}

.knowledge-source-card__article-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #f4f5f7;
  border: 1px solid #eaecf0;
  box-sizing: border-box;
}

.knowledge-source-card__article-icon,
.knowledge-source-card__overlay-graphic {
  width: 64px;
  height: 64px;
  display: block;
  object-fit: contain;
}

.knowledge-source-card__video-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  background: rgba(16, 24, 40, 0.12);
}

.knowledge-source-card__body {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 2px;
}

.knowledge-source-card__description {
  overflow: hidden;
  color: #475467;
  text-align: justify;
  text-overflow: ellipsis;
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  line-clamp: 4;
  box-orient: vertical;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}
</style>
